import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { OrderStatus } from "@/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Shared cart key generator — single source of truth
export function cartKey(productId: string, variantId?: string): string {
  return `${productId}:${variantId ?? ""}`;
}

// Shared shipping calculation
export const FREE_SHIPPING_THRESHOLD = 500;
export const SHIPPING_COST = 29.9;
export function calcShipping(subtotal: number): number {
  return subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_COST;
}

// Order status labels & colors — single source of truth
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  Pending: "Beklemede",
  Processing: "Hazırlanıyor",
  Shipped: "Kargoda",
  Delivered: "Teslim Edildi",
  Cancelled: "İptal Edildi",
  Refunded: "İade Edildi",
  OnHold: "Bekletiliyor",
};

export const ORDER_STATUS_CLASSES: Record<OrderStatus, string> = {
  Pending: "bg-yellow-100 text-yellow-700",
  Processing: "bg-blue-100 text-blue-700",
  Shipped: "bg-purple-100 text-purple-700",
  Delivered: "bg-green-100 text-green-700",
  Cancelled: "bg-red-100 text-red-700",
  Refunded: "bg-gray-100 text-gray-700",
  OnHold: "bg-orange-100 text-orange-700",
};

export function formatCurrency(amount: number, currency = "TRY"): string {
  const number = new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  const symbols: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
  const symbol = symbols[currency] ?? currency;
  return `${number} ${symbol}`;
}

export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat("tr-TR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date(date));
}

export function truncate(text: string, length = 80): string {
  return text.length > length ? text.slice(0, length) + "…" : text;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ş/g, "s")
    .replace(/ı/g, "i").replace(/ö/g, "o").replace(/ç/g, "c")
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function discountPercent(price: number, compareAtPrice?: number): number {
  if (!compareAtPrice || compareAtPrice <= price) return 0;
  return Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
}

const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:5193";

/**
 * API'den gelen görsel URL'ini absolute URL'e çevirir.
 * Zaten http/https ile başlıyorsa dokunmaz.
 */
export function mediaUrl(url?: string | null): string {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_BASE}${url}`;
}
